import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';

import { injectIntl } from 'react-intl';

import CurrencyDropdown from 'components/CurrencyDropdown';

import { getLocaleInDBFormat, request, filterExcludedCurrencies } from 'utils';
import { CURRENCIES_OPTIONS } from 'constants/currency';

const CurrencyConverterDropdown = ({
  intl,
  onScrollEnd,
  onSelectOption,
  currencyInfo,
  loading,
  items,
  optionType,
  containerStyle,
  listWrapperStyle,
  isButtonMode,
}) => {
  const [searchItems, setSearchItems] = useState([]);
  const [searchCount, setSearchCount] = useState(0);
  const [searchLoading, setSearchLoadingStatus] = useState(false);
  const [initSearch, setInitSearchStatus] = useState(false);
  const [isFocused, setIsFocusedStatus] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [currenciesType, setCurrenciesType] = useState(
    CURRENCIES_OPTIONS.money
  );
  const [isOpen, setIsOpenStatus] = useState(false);
  const dropdownRef = useRef();
  const hasUserInteractedRef = useRef(false);

  const translates = useMemo(() => {
    const currenciesTypesOptions = Object.values(CURRENCIES_OPTIONS).map(
      type => ({
        value: type,
        label: intl.formatMessage({ id: `currenciesType.${type}` }),
      })
    );
    const inputPlaceholder = intl.formatMessage({
      id: `currencyDropdown.searchInput.placeholder`,
    });
    const inputLabel = intl.formatMessage({
      id: `currencyDropdown.searchInput.label`,
    });
    const cancelButtonLabel = intl.formatMessage({
      id: 'currencyDropdown.searchInput.cancelButtonLabel',
    });
    const noData1 = intl.formatMessage({
      id: 'noData.noFound',
    });
    const noData2 = intl.formatMessage({
      id: 'noData.tryAnother',
    });
    const closeButtonTitle = intl.formatMessage({ id: 'close' });
    const favoritesLabel = intl.formatMessage({ id: 'favorites' });
    const starTitle = intl.formatMessage({ id: 'addToFavorites' });
    return {
      currenciesTypesOptions,
      inputPlaceholder,
      inputLabel,
      cancelButtonLabel,
      noData1,
      noData2,
      closeButtonTitle,
      favoritesLabel,
      starTitle,
    };
  }, []);

  useEffect(() => {
    if (!hasUserInteractedRef.current) {
      return;
    }

    onSearch(searchValue);
  }, [searchValue]);

  // eslint-disable-next-line
  const search = async (searchValue, isPaginationFetch = false) => {
    const shouldFetch = searchItems.length < searchCount;

    try {
      if (!shouldFetch && isPaginationFetch) {
        return true;
      }

      setSearchLoadingStatus(true);

      const start = isPaginationFetch ? searchItems.length : 0;

      const response = await request({
        url: `${process.env.APP_CURRENCIES_API_URL}/list`,
        params: {
          start,
          cpp: 15,
          search: searchValue,
          locale: getLocaleInDBFormat(intl.locale),
        },
      });

      setSearchItems(
        isPaginationFetch ? [...searchItems, ...response.list] : response.list
      );
      setInitSearchStatus(true);
      setSearchCount(response.pagination.count);
    } catch (error) {
      console.log(error);
    }
    setSearchLoadingStatus(false);
  };

  const onSearch = useCallback(debounce(search, 500), []);

  const onFocus = () => {
    setIsFocusedStatus(true);
  };

  const onBlur = () => {
    setIsFocusedStatus(false);
  };

  const onSearchInputChange = event => {
    const { value } = event.target;
    hasUserInteractedRef.current = true;
    setSearchValue(value);
  };

  const onCancel = event => {
    event.stopPropagation();
    setSearchValue('');
    setIsFocusedStatus(false);
  };

  const onChangeVisibility = async isVisible => {
    if (isVisible) {
      if (searchValue) {
        search(searchValue, true);
      } else {
        onScrollEnd(currenciesType);
      }
    }
  };

  const onSelectCurrenciesType = value => {
    setCurrenciesType(value);
  };

  const onClickOption = event => {
    const { value } = event.currentTarget.dataset;
    const { slug, type } = JSON.parse(value);
    const generatedValue = `${slug}-${type}`;

    toggleDropdown();
    onSelectOption(generatedValue);
  };

  const toggleDropdown = e => {
    if (e) {
      e.stopPropagation();
    }
    setIsOpenStatus(!isOpen);
    setIsFocusedStatus(isOpen ? false : isFocused);
    setInitSearchStatus(false);
    setSearchValue('');
    setSearchLoadingStatus(false);
    setSearchItems([]);
  };

  return (
    <CurrencyDropdown
      dropdownRef={dropdownRef}
      currencyInfo={currencyInfo}
      isOpen={isOpen}
      toggleDropdown={toggleDropdown}
      isFocused={isFocused}
      searchValue={searchValue}
      currenciesTypesOptions={translates.currenciesTypesOptions}
      currenciesType={currenciesType}
      onSelectCurrenciesType={onSelectCurrenciesType}
      onChangeVisibility={onChangeVisibility}
      loading={loading}
      searchLoading={searchLoading}
      items={useMemo(
        () => ({
          ...items,
          all: filterExcludedCurrencies(items.all),
          money: filterExcludedCurrencies(items.money),
          crypto: filterExcludedCurrencies(items.crypto),
        }),
        [items]
      )}
      onSelectOption={onClickOption}
      searchItems={filterExcludedCurrencies(searchItems)}
      initSearch={initSearch}
      onFocus={onFocus}
      onBlur={onBlur}
      onSearchInputChange={onSearchInputChange}
      onCancel={onCancel}
      optionType={optionType}
      containerStyle={containerStyle}
      listWrapperStyle={listWrapperStyle}
      isButtonMode={isButtonMode}
    />
  );
};

CurrencyConverterDropdown.propTypes = {
  currencyInfo: PropTypes.objectOf(PropTypes.string).isRequired,
  items: PropTypes.shape({
    all: PropTypes.arrayOf(PropTypes.object),
    money: PropTypes.arrayOf(PropTypes.object),
    crypto: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  onScrollEnd: PropTypes.func.isRequired,
  onSelectOption: PropTypes.func.isRequired,
  optionType: PropTypes.string,
  containerStyle: PropTypes.shape({}),
  listWrapperStyle: PropTypes.shape({}),
  isButtonMode: PropTypes.bool,

  intl: PropTypes.shape({
    locale: PropTypes.string,
    formatMessage: PropTypes.func,
  }).isRequired,
};

CurrencyConverterDropdown.defaultProps = {
  containerStyle: {},
  listWrapperStyle: {},
  optionType: undefined,
  isButtonMode: false,
};

export default injectIntl(CurrencyConverterDropdown);
